import { Workspace } from '@sr-sdks/api-sdk-axios';
import {
  Alert,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Select,
  SingleOption,
} from '@sr/ui-library';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Control } from 'react-hook-form';
import { queryOptionsFindWorkspaceUsers } from '../../../../api/permissions/userApi';
import {
  WorkspaceContactFieldName,
  getVisibleWorkspaceContactFields,
} from '../../../../components/WorkspaceDetails/ContactDetails/contactDetailsConfig';
import { WORKSPACE_DETAILS_FORM_FIELDS } from '../../shared/fieldNames';
import { WorkspaceDetailsFormValues } from '../../shared/types';

interface ContactRoleFieldProps {
  control: Control<WorkspaceDetailsFormValues>;
  label: string;
  name: WorkspaceContactFieldName;
  options: SingleOption[];
}

/**
 * Generic contact-role select. All five contact roles share this component —
 * labels, visibility, and DTO keys come from contactDetailsConfig.
 */
export const ContactRoleField = ({
  control,
  label,
  name,
  options,
}: ContactRoleFieldProps) => (
  <FormField
    name={`${WORKSPACE_DETAILS_FORM_FIELDS.contacts}.${name}`}
    control={control}
    render={({ field }) => (
      <FormItem>
        <FormLabel>{label}</FormLabel>
        <FormControl>
          <Select
            allowDeselect={false}
            options={options}
            placeholder="Select a teammate"
            value={field.value ?? ''}
            onValueChange={field.onChange}
          />
        </FormControl>
        <FormMessage />
      </FormItem>
    )}
  />
);

interface ContactRoleFieldsProps {
  control: Control<WorkspaceDetailsFormValues>;
  workspace: Workspace;
}

/**
 * Renders every visible contact-role select. Owns the teammates query and
 * option mapping so WorkspaceDetailsContactSection stays presentation-only.
 * Rendered directly inside the section's grid — the alerts span both columns.
 */
export const ContactRoleFields = ({
  control,
  workspace,
}: ContactRoleFieldsProps) => {
  const { data: teammates, isError } = useQuery(
    queryOptionsFindWorkspaceUsers(workspace._id),
  );

  const teammateOptions = useMemo<SingleOption[]>(
    () =>
      teammates?.map((teammate) => ({
        label: `${teammate.firstName} ${teammate.lastName} (${teammate.emailAddress})`,
        value: teammate._id,
      })) ?? [],
    [teammates],
  );

  const visibleContactFields = getVisibleWorkspaceContactFields(workspace);

  if (isError) {
    return (
      // Without the teammate list the selects would render blank even
      // for assigned roles — surface the failure instead of misleading.
      <Alert
        title="Unable to load teammates"
        className="col-span-full"
        variant="error"
      >
        Contact assignments can&apos;t be shown right now. Please refresh the
        page or try again later.
      </Alert>
    );
  }

  return (
    <>
      <Alert title="Heads up!" className="col-span-full" variant="info">
        You can assign other teammates as contacts once they&apos;ve been
        onboarded onto this workspace.
      </Alert>

      {visibleContactFields.map((field) => (
        <ContactRoleField
          key={field.name}
          label={field.label}
          name={field.name}
          control={control}
          options={teammateOptions}
        />
      ))}
    </>
  );
};
